import { useState, useEffect, useMemo } from 'react'
import { Form } from 'antd'
import { useRecoilValue } from 'recoil'
import healthClaimFormAtom from '@/recoil/healthClaimForm/atom'
import { HealthClaimModeEnum } from '@/constant/E_CLAIM'
import { useHealthClaimForm } from '@/contexts/healthClaimForm'
import { useInterestCode } from '@/services/healthClaim/useInterestCode'
import { useClaimTypes } from '@/services/healthClaim/useClaimTypes'
import { InterestCode } from '@/services/healthClaim/useInterestCode/types'
import { ClaimType } from '@/services/healthClaim/useClaimTypes/types'
import { isIncompleteCoverageDataError } from '@/libs/utils/claim-error-notification.util'

export interface BenefitItem {
  key: string
  no: number | string
  code: string
  description: string
  maxClaimAmount?: number | string
  remainingClaimBalance?: number | string
  receiptAmount?: number
  coverageAmount?: number
  children?: BenefitItem[]
}

export const MedicalExpenseTableHook = () => {
  const {
    forms: { treatmentDetailForm },
    updateMedicalExpenseItem,
    getMedicalExpenseTotal,
    initializeMedicalExpenses,
    medicalExpenses,
    getMedicalExpenseItem,
    selectedPolicyNumber,
    setIsCoverageDataIncomplete,
  } = useHealthClaimForm()
  const moneyFormat = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  const healthClaim = useRecoilValue(healthClaimFormAtom)
  // Watch form fields to ensure reactivity when user updates the form
  const treatmentTypeId: string | undefined = Form.useWatch('treatmentTypeId', treatmentDetailForm)
  const treatmentDateIn: Dayjs | string | undefined = Form.useWatch(
    'treatmentDateIn',
    treatmentDetailForm,
  )
  const policyNumber = selectedPolicyNumber || ''

  // Get data from state as fallback using useMemo to prevent dependency array issues
  const hasPolicyOverride = Boolean(healthClaim.selectedPolicyNumberOverride)

  const stateInterestCodes = useMemo(
    () => (hasPolicyOverride ? [] : healthClaim.data?.interestCodes || []),
    [hasPolicyOverride, healthClaim.data?.interestCodes],
  )
  const stateClaimTypes = useMemo(
    () => (hasPolicyOverride ? [] : healthClaim.data?.claimTypes || []),
    [hasPolicyOverride, healthClaim.data?.claimTypes],
  )

  const [isNewPolicyType, setIsNewPolicyType] = useState<boolean | null>(null)
  const [apiData, setApiData] = useState<InterestCode[] | ClaimType[]>([])
  const [searchText, setSearchText] = useState('')
  const isCreateOrEditMode =
    healthClaim.mode === HealthClaimModeEnum.CREATE || healthClaim.mode === HealthClaimModeEnum.EDIT

  // Call useInterestCode only after treatment type is selected
  // Gate by passing empty policy number when no treatmentTypeId to disable the query internally
  const interestCodeQuery = useInterestCode(
    treatmentTypeId ? policyNumber : '',
    treatmentDateIn || '',
  )
  const isCoverageDataIncomplete = isIncompleteCoverageDataError(interestCodeQuery.error)

  // Call useClaimTypes only after treatment type is selected
  const claimTypesQuery = useClaimTypes(treatmentTypeId, {
    enabled: !!treatmentTypeId,
  })

  useEffect(() => {
    setIsCoverageDataIncomplete?.(isCoverageDataIncomplete)
  }, [isCoverageDataIncomplete, setIsCoverageDataIncomplete])

  useEffect(() => {
    // Priority 1: Always try API calls first to get latest data
    if (policyNumber) {
      // Check interest code query status
      if (
        interestCodeQuery.isSuccess &&
        interestCodeQuery.data &&
        interestCodeQuery.data.length > 0
      ) {
        setIsNewPolicyType(true)
        setApiData(interestCodeQuery.data)
      } else if (isCoverageDataIncomplete) {
        setApiData([])
        setIsNewPolicyType(null)
      } else if (
        interestCodeQuery.isError ||
        (interestCodeQuery.isSuccess &&
          (!interestCodeQuery.data || interestCodeQuery.data.length === 0))
      ) {
        // Priority 2: If interest code API fails, try using state data as fallback
        if (stateInterestCodes.length > 0) {
          // Using interestCodes from state as fallback
          setIsNewPolicyType(true)
          // Convert state format to API format
          const mappedInterestCodes = stateInterestCodes.map((item) => ({
            interestCode: item.interestCode,
            details: `Interest Code: ${item.interestCode}`,
            maxClaimAmount: item.maxClaimAmount.toString(),
            remainingClaimBalance: item.remainingClaimBalance.toString(),
            limitCheckingInterest: '', // May need to check if state has this field
          }))
          setApiData(mappedInterestCodes as InterestCode[])
        } else if (
          treatmentTypeId &&
          claimTypesQuery.isSuccess &&
          claimTypesQuery.data &&
          Array.isArray(claimTypesQuery.data) &&
          claimTypesQuery.data.length > 0
        ) {
          setIsNewPolicyType(false)
          setApiData(claimTypesQuery.data as ClaimType[])
        } else if (claimTypesQuery.isError && stateClaimTypes.length > 0) {
          // Priority 3: If claimTypes API fails but has state data
          setIsNewPolicyType(false)
          // Convert state format to API format
          const mappedClaimTypes = stateClaimTypes.map((item) => ({
            id: item.id,
            code: item.id,
            detail: `Claim Type: ${item.id}`,
            coverageAmount: item.coverageAmount,
          }))
          setApiData(mappedClaimTypes as ClaimType[])
        } else {
          // Both APIs failed, set empty data
          setIsNewPolicyType(false)
          setApiData([])
        }
      }
    } else {
      // No policyNumber available, try claim types or use state data
      if (
        treatmentTypeId &&
        claimTypesQuery.isSuccess &&
        claimTypesQuery.data &&
        Array.isArray(claimTypesQuery.data) &&
        claimTypesQuery.data.length > 0
      ) {
        setIsNewPolicyType(false)
        setApiData(claimTypesQuery.data as ClaimType[])
      } else if (stateClaimTypes.length > 0) {
        // Using claimTypes from state (no policyNumber)
        setIsNewPolicyType(false)
        const mappedClaimTypes = stateClaimTypes.map((item) => ({
          id: item.id,
          code: item.id,
          detail: `Claim Type: ${item.id}`,
          coverageAmount: item.coverageAmount,
        }))
        setApiData(mappedClaimTypes as ClaimType[])
      }
    }
  }, [
    policyNumber,
    treatmentDateIn,
    treatmentTypeId,
    interestCodeQuery.isSuccess,
    interestCodeQuery.isError,
    interestCodeQuery.data,
    interestCodeQuery.error,
    claimTypesQuery.isSuccess,
    claimTypesQuery.isError,
    claimTypesQuery.data,
    stateInterestCodes,
    stateClaimTypes,
    isCoverageDataIncomplete,
  ])

  // Clear table source when switching to create mode
  useEffect(() => {
    if (healthClaim.mode === HealthClaimModeEnum.CREATE) {
      setApiData([])
      setIsNewPolicyType(null)
    }
  }, [healthClaim.mode])

  // Clear immediately when switching policy to avoid showing stale data
  useEffect(() => {
    setApiData([])
    setIsNewPolicyType(null)
  }, [policyNumber])

  // Clear when treatment type is not selected
  useEffect(() => {
    if (!treatmentTypeId) {
      setApiData([])
      setIsNewPolicyType(null)
    }
  }, [treatmentTypeId])

  // Transform API data to table format
  const transformedData = useMemo(() => {
    if (isNewPolicyType === true && apiData.length > 0) {
      // Transform InterestCode data for new policy type with hierarchical structure
      const interestCodeData = apiData as InterestCode[]

      // First, create a map of all items
      const itemsMap = new Map<string, BenefitItem>()

      // Transform all items to BenefitItem format
      interestCodeData.forEach((item, index) => {
        const existingExpense = medicalExpenses.find((exp) => exp.key === item.interestCode)
        const stateData = stateInterestCodes.find(
          (state) => state.interestCode === item.interestCode,
        )

        const transformedItem: BenefitItem = {
          key: item.interestCode,
          no: index + 1,
          code: item.interestCode,
          description: item.details,
          maxClaimAmount: parseFloat(item.maxClaimAmount.replace(/,/g, '')) || 0,
          remainingClaimBalance: parseFloat(item.remainingClaimBalance.replace(/,/g, '')) || 0,
          // Priority order: user input > state data > default
          receiptAmount: existingExpense?.receiptAmount ?? stateData?.receiptAmount ?? 0,
          coverageAmount: existingExpense?.coverageAmount ?? stateData?.coverageAmount ?? 0,
          children: [],
        }
        itemsMap.set(item.interestCode, transformedItem)
      })

      // Build hierarchical structure
      const rootItems: BenefitItem[] = []

      interestCodeData.forEach((item) => {
        const transformedItem = itemsMap.get(item.interestCode)!

        if (!item.limitCheckingInterest || item.limitCheckingInterest === '') {
          // This is a root item (parent)
          rootItems.push(transformedItem)
        } else {
          // This is a child item, find its parent
          const parent = itemsMap.get(item.limitCheckingInterest)
          if (parent) {
            // Set child item number to empty (as per your original mock data)
            transformedItem.no = ''
            parent.children!.push(transformedItem)
          } else {
            // Parent not found, treat as root item
            rootItems.push(transformedItem)
          }
        }
      })

      // Update numbering for root items only and clean up empty children arrays
      rootItems.forEach((item, index) => {
        item.no = index + 1
        // Remove children array if it's empty to avoid expansion issues
        if (item.children && item.children.length === 0) {
          delete item.children
        }
      })

      return rootItems
    } else if (isNewPolicyType === false && apiData.length > 0) {
      // Transform ClaimType data for old policy type
      const transformed = (apiData as ClaimType[]).map((item, index) => {
        const existingExpense = medicalExpenses.find((exp) => exp.key === item.id)
        const stateData = stateClaimTypes.find((state) => state.id === item.id)

        return {
          key: item.id,
          no: index + 1,
          code: item.code,
          description: item.detail,
          // Priority order: user input > state data > API data > default
          receiptAmount: existingExpense?.receiptAmount ?? 0,
          coverageAmount: existingExpense?.coverageAmount ?? stateData?.coverageAmount ?? 0,
        }
      })
      return transformed
    }
    return []
  }, [isNewPolicyType, apiData, medicalExpenses, stateInterestCodes, stateClaimTypes])

  // Initialize medical expenses when API data is available
  useEffect(() => {
    if (isNewPolicyType !== null && apiData.length > 0 && medicalExpenses.length === 0) {
      initializeMedicalExpenses(apiData, isNewPolicyType)
    }
  }, [isNewPolicyType, apiData, medicalExpenses.length, initializeMedicalExpenses])

  // Apply search filter
  const filteredData = useMemo(() => {
    // Helper function to recursively search in hierarchical data
    const searchInHierarchy = (items: BenefitItem[], searchTerm: string): BenefitItem[] => {
      if (!searchTerm) return items

      const filtered: BenefitItem[] = []

      items.forEach((item) => {
        const matchesSearch =
          item.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.description.toLowerCase().includes(searchTerm.toLowerCase())

        if (matchesSearch) {
          // If parent matches, include with all children
          filtered.push(item)
        } else if (item.children) {
          // If parent doesn't match, check children
          const filteredChildren = searchInHierarchy(item.children, searchTerm)
          if (filteredChildren.length > 0) {
            // Include parent if any child matches
            filtered.push({
              ...item,
              children: filteredChildren,
            })
          }
        }
      })

      return filtered
    }

    if (!searchText.trim()) return transformedData
    return searchInHierarchy(transformedData, searchText.trim())
  }, [transformedData, searchText])

  const data = filteredData

  // Loading state
  const isLoading = !!treatmentTypeId && (interestCodeQuery.isLoading || claimTypesQuery.isLoading)

  // Search placeholder text
  const searchPlaceholder = isNewPolicyType
    ? 'ค้นหา Interest code, รายการ'
    : 'ค้นหา Claim type, รายการ'

  return {
    data,
    isLoading,
    isCreateOrEditMode,
    isNewPolicyType,
    isCoverageDataIncomplete,
    filteredData,
    searchText,
    setSearchText,
    searchPlaceholder,
    updateMedicalExpenseItem,
    getMedicalExpenseTotal,
    getMedicalExpenseItem,
    moneyFormat,
  }
}
